import { useEffect, useState, useCallback } from "react";
import { Joyride, STATUS, EVENTS } from "react-joyride";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const STORAGE_KEY_PREFIX = "rv_tour_done_";
const DISABLED_KEY_PREFIX = "rv_tour_disabled_";

/**
 * Deterministic first-visit walkthrough for investors.
 *
 * Storage strategy: per-user localStorage flag (`rv_tour_done_<userId>`).
 * Simple, survives refresh, no backend call. A missing flag + role=landlord
 * auto-starts the tour on /dashboard. The Settings page exposes a manual
 * "Restart tour" button that clears this flag and sends the user back to
 * /dashboard with `?tour=start` so ProductTour re-fires.
 */
const steps = [
  {
    target: "body",
    placement: "center",
    title: "Welcome to RealtyVault Manager",
    content:
      "This 2-minute tour shows you the five things most investors do in their first session. You can skip any time and restart from Settings.",
    disableBeacon: true,
  },
  {
    target: '[data-testid="nav-properties"]',
    placement: "right",
    title: "1. Add your first property",
    content:
      "Everything else (leases, loans, expenses, valuation) links back to a property. Address auto-geocodes + pulls a Street View photo.",
    disableBeacon: true,
  },
  {
    target: '[data-testid="nav-tenants"]',
    placement: "right",
    title: "2. Invite your tenants",
    content:
      "Each tenant gets their own portal to pay rent online and file maintenance requests.",
    disableBeacon: true,
  },
  {
    target: '[data-testid="nav-loans"]',
    placement: "right",
    title: "3. Track your financing",
    content:
      "Record mortgages + commercial loans for auto-generated amortization, maturity alerts, and refi savings at today's market rate.",
    disableBeacon: true,
  },
  {
    target: '[data-testid="nav-reports"]',
    placement: "right",
    title: "4. Generate investor reports",
    content:
      "One-click quarterly bundles: rent roll, loan portfolio, portfolio snapshot, and expense report. Email to your CPA + co-investors.",
    disableBeacon: true,
  },
  {
    target: '[data-testid="nav-settings"]',
    placement: "right",
    title: "5. Connect Stripe",
    content:
      "Open Settings to wire up Stripe Connect. Once connected, your tenants see a 'Pay rent' button and payouts go straight to your bank.",
    disableBeacon: true,
  },
  {
    target: '[data-testid="nav-help"]',
    placement: "right",
    title: "Need more detail?",
    content:
      "The full user manual lives here. Searchable, printable, and covers every feature. You can restart this tour anytime from Settings.",
    disableBeacon: true,
  },
];

export default function ProductTour() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [run, setRun] = useState(false);

  // Start logic: only run the tour when ?tour=start is explicitly in the URL
  // (i.e. the user clicked "Restart tour" in Settings). Tour does not
  // auto-launch on first visit; the per-user "disabled" toggle in Settings
  // controls whether the manual restart still fires.
  useEffect(() => {
    if (!user || user.role !== "landlord") return;
    const params = new URLSearchParams(window.location.search);
    const explicit = params.get("tour") === "start";
    if (explicit) {
      // Small delay so the sidebar renders + Joyride can resolve target selectors
      const t = setTimeout(() => setRun(true), 500);
      return () => clearTimeout(t);
    }
  }, [user]);

  const handleCallback = useCallback(
    (data) => {
      const { status, type } = data;
      if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
        if (user) localStorage.setItem(STORAGE_KEY_PREFIX + user.id, "1");
        setRun(false);
        // Only mark backend-completed on a *real* finish — skipped tours don't
        // count toward the admin onboarding-funnel KPI.
        if (status === STATUS.FINISHED) {
          api.post("/me/tour-completed").catch(() => {
            // Non-fatal — the localStorage flag still prevents re-prompts.
          });
        }
        // Clean up the `?tour=start` param on completion
        if (window.location.search.includes("tour=start")) {
          navigate("/dashboard", { replace: true });
        }
      }
      // Handle errors (missing target) gracefully by advancing past them
      if (type === EVENTS.TARGET_NOT_FOUND) {
        // eslint-disable-next-line no-console
        console.warn("[ProductTour] target not found, skipping step", data);
      }
    },
    [user, navigate]
  );

  if (!user || user.role !== "landlord") return null;

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showSkipButton
      showProgress
      scrollToFirstStep
      disableOverlayClose
      callback={handleCallback}
      locale={{
        back: "Back",
        close: "Close",
        last: "Finish",
        next: "Next",
        skip: "Skip tour",
      }}
      styles={{
        options: {
          primaryColor: "#0f172a",
          textColor: "#0f172a",
          backgroundColor: "#ffffff",
          arrowColor: "#ffffff",
          overlayColor: "rgba(15, 23, 42, 0.55)",
          zIndex: 10000,
        },
        tooltipContainer: {
          textAlign: "left",
        },
        tooltipTitle: {
          fontFamily: "Outfit, sans-serif",
          fontSize: "16px",
          fontWeight: 700,
        },
        tooltipContent: {
          fontSize: "14px",
          lineHeight: "1.5",
          color: "#475569",
        },
        buttonNext: {
          borderRadius: 2,
          fontFamily: "monospace",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          fontSize: "11px",
          padding: "8px 14px",
        },
        buttonBack: {
          color: "#64748b",
          fontFamily: "monospace",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          fontSize: "11px",
        },
        buttonSkip: {
          color: "#94a3b8",
          fontFamily: "monospace",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          fontSize: "11px",
        },
      }}
    />
  );
}

/** Helper for the Settings "Restart tour" button */
export function restartTour(userId, navigate) {
  if (userId) {
    localStorage.removeItem(STORAGE_KEY_PREFIX + userId);
    // Restart implies user wants the tour — clear the disabled flag too
    localStorage.removeItem(DISABLED_KEY_PREFIX + userId);
  }
  navigate("/dashboard?tour=start");
}

/** Helpers for the Settings "Show product tour" toggle */
export function isTourDisabled(userId) {
  if (!userId) return false;
  return localStorage.getItem(DISABLED_KEY_PREFIX + userId) === "1";
}

export function setTourDisabled(userId, disabled) {
  if (!userId) return;
  if (disabled) {
    localStorage.setItem(DISABLED_KEY_PREFIX + userId, "1");
    // Disabling implies "don't auto-show" — also mark seen so a fresh login
    // doesn't try to re-fire it before the disabled flag is read.
    localStorage.setItem(STORAGE_KEY_PREFIX + userId, "1");
  } else {
    localStorage.removeItem(DISABLED_KEY_PREFIX + userId);
    // Enabling means "show on next /dashboard visit" — clear the seen flag
    // so the auto-start path fires.
    localStorage.removeItem(STORAGE_KEY_PREFIX + userId);
  }
}
